/**
 * Core model of the profile-export wizard: format, data-type and column
 * selections, their defaults, and the pure derivations the saga and UI share.
 * Pure: no React/Redux/fetch.
 */
import type { CompanyExportField, ExportDataType, ProfileExportField } from '@shared/schemas';
import { COMPANY_EXPORT_FIELDS, PROFILE_EXPORT_FIELDS } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

export type ExportFormat = 'csv' | 'xlsx' | 'json';

/** The backend serialises `both` in one response; CSV splits it client-side. */
export type ResolvedDataType = ExportDataType | 'both';

/** MIME type (client-side Blob wrapping) + file extension per format. */
export const EXPORT_FORMAT_CONFIG: Record<ExportFormat, { mime: string; ext: string }> = {
  csv: { mime: 'text/csv;charset=utf-8;', ext: 'csv' },
  xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ext: 'xlsx' },
  json: { mime: 'application/json;charset=utf-8;', ext: 'json' },
};

/** The backend exports at most 5 000 rows; the UI shows the capped figure. */
export const EXPORT_ROW_CAP = 5000;

export const capProfileCount = (count: number): number => Math.min(count, EXPORT_ROW_CAP);

/** Filter used when the page is opened without navigation state (direct visit). */
export const EMPTY_FILTER_GROUP: FilterGroup = {
  id: 'root',
  kind: 'group',
  combinator: 'and',
  children: [],
};

// ── Defaults: profiles pre-selected, every column checked ──

export const DEFAULT_DATA_TYPES: ExportDataType[] = ['profiles'];
export const defaultProfileColumns = (): ProfileExportField[] => [...PROFILE_EXPORT_FIELDS];
export const defaultCompanyColumns = (): CompanyExportField[] => [...COMPANY_EXPORT_FIELDS];

/**
 * Collapse the data-type selection into the single `data_type` value the
 * backend understands: both types selected → 'both'.
 */
export function resolveDataType(dataTypes: ExportDataType[]): ResolvedDataType {
  const wantBoth = dataTypes.includes('profiles') && dataTypes.includes('companies');
  return wantBoth ? 'both' : (dataTypes[0] ?? 'profiles');
}

/**
 * Toggle a data type in the selection; at least one must always remain, so
 * removing the last one is a no-op (returns the selection unchanged).
 */
export function toggleDataType(
  current: ExportDataType[],
  dataType: ExportDataType,
): ExportDataType[] {
  if (current.includes(dataType)) {
    if (current.length <= 1) return current;
    return current.filter((d) => d !== dataType);
  }
  return [...current, dataType];
}

/** Toggle a single column in a selection. */
export function toggleColumn<F extends string>(current: F[], field: F): F[] {
  return current.includes(field) ? current.filter((f) => f !== field) : [...current, field];
}

/** Check / uncheck a whole group of columns at once (group checkbox). */
export function setGroupColumns<F extends string>(
  current: F[],
  fields: F[],
  checked: boolean,
): F[] {
  if (checked) {
    return [...current, ...fields.filter((f) => !current.includes(f))];
  }
  return current.filter((f) => !fields.includes(f));
}
